import { useEffect, useState, useCallback } from 'react';
import { OTA_NAME_OPTIONS as STATIC_OTA_OPTIONS } from '../constants/dropdownOptions';

const API = process.env.REACT_APP_BACKEND_URL;

// Module-level cache so multiple LeadModal instances hit the network only
// once per page-load. Refetched explicitly via the `reload()` helper.
let _cache = null;
let _inFlight = null;

const authHeader = () => {
  const t = localStorage.getItem('token') || localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fetchOnce = async () => {
  if (_cache) return _cache;
  if (_inFlight) return _inFlight;
  _inFlight = (async () => {
    try {
      const res = await fetch(`${API}/api/ota-options`, { headers: authHeader() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const rows = Array.isArray(data?.data) ? data.data : [];
      // Backend rows shape: { id, value, label, ... }. Convert to the
      // {value,label} shape the dropdowns already expect.
      _cache = rows.map(r => ({ value: r.value, label: r.label }));
      return _cache;
    } catch (_e) {
      // Network / not authenticated / server down — fall back so the UI
      // remains functional. Fallback deliberately excludes the leading
      // "Select OTA" placeholder — callers add their own placeholder.
      _cache = STATIC_OTA_OPTIONS.filter(o => o.value !== '');
      return _cache;
    } finally {
      _inFlight = null;
    }
  })();
  return _inFlight;
};

export const invalidateOtaCache = () => { _cache = null; };

/**
 * useOtaOptions — returns the dynamic OTA dropdown list for Accounts.
 *
 * The list is the SINGLE SOURCE OF TRUTH sourced from Mongo (via
 * /api/ota-options). Historical OTA values that are no longer present in
 * the config are still surfaced when `historicalValues` is provided so
 * that Edit Account continues to display saved OTA names correctly.
 *
 * @param {string[]} historicalValues – OTA `value`s currently saved on the
 *   record being viewed/edited. Missing ones are appended so the dropdown
 *   still shows their label (or the raw value if no label is known).
 * @returns {{ options: {value,label}[], loading: boolean, reload: () => void }}
 */
export function useOtaOptions(historicalValues = []) {
  const [options, setOptions] = useState(_cache || null);
  const [loading, setLoading] = useState(!_cache);

  const load = useCallback(async () => {
    setLoading(true);
    const rows = await fetchOnce();
    setOptions(rows);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const merged = (() => {
    const base = options || STATIC_OTA_OPTIONS.filter(o => o.value !== '');
    if (!historicalValues || historicalValues.length === 0) return base;
    const known = new Set(base.map(o => o.value));
    const extras = historicalValues
      .filter(v => v && !known.has(v))
      .map(v => {
        // Try to resolve a friendly label from the static list, else show
        // the raw value with a "(removed)" hint so admins understand the
        // OTA no longer exists in the current configuration.
        const stat = STATIC_OTA_OPTIONS.find(o => o.value === v);
        return { value: v, label: stat ? stat.label : `${v} (removed)` };
      });
    return [...base, ...extras];
  })();

  const reload = useCallback(() => {
    invalidateOtaCache();
    return load();
  }, [load]);

  return { options: merged, loading, reload };
}

export default useOtaOptions;
